import { injectable, inject } from 'inversify';
import HttpClient from '../../../http';
import {
  GetProductDetailParams,
  GetProductsByTagParams,
  GetProductsOnPromotionRequestParams,
  GetProductsOnPromotionResponse,
  ProductListingRequestParams,
  ProductListingResponse,
  ProductsServiceInterface,
  StoreStockRequestParams,
  StoreStockResponse,
  StoreSubcategoryStockRequestParams,
} from './ProductsService.types';
import { backendUrl } from 'src/config';
import { Product } from 'src/types/common';

@injectable()
class ProductsService implements ProductsServiceInterface {
  @inject(HttpClient) private client!: HttpClient;

  async getStoreStock({
    storeSlug,
    search,
    categorySlug,
    ordering,
    limit,
    offset,
    token,
    product__brand__slug_name,
  }: StoreStockRequestParams) {
    return await this.client
      .getClient()
      .get<StoreStockResponse>(`/stock/${storeSlug}/products/`, {
        ...(token && {
          headers: {
            Authorization: `Token ${token}`,
          },
        }),
        baseURL: backendUrl,
        params: {
          search,
          product__main_category__slug_name: categorySlug,
          ordering,
          limit,
          offset,
          product__brand__slug_name,
        },
      });
  }

  async getProductsListing({
    ordering,
    storeSlug,
  }: ProductListingRequestParams) {
    return await this.client
      .getClient()
      .get<ProductListingResponse>(`/stock/${storeSlug}/products/highlighted`, {
        baseURL: backendUrl,
        params: { ordering },
      });
  }

  async getSubcategoryStock({
    storeSlug,
    subcategorySlug,
    search,
    ordering,
    limit,
    token,
  }: StoreSubcategoryStockRequestParams) {
    return await this.client
      .getClient()
      .get<StoreStockResponse>(`/stock/${storeSlug}/products/`, {
        ...(token && {
          headers: {
            Authorization: `Token ${token}`,
          },
        }),
        baseURL: backendUrl,
        params: {
          search,
          product__categories__slug_name: subcategorySlug,
          ordering,
          limit,
        },
      });
  }

  public getProductsOnPromotion({
    storeSlug,
    group_by,
    category,
    ...params
  }: GetProductsOnPromotionRequestParams) {
    const queryParams = Object.fromEntries(
      Object.entries({ group_by, category, ...params }).filter(
        ([, value]) => value !== undefined && value !== null
      )
    );

    return this.client
      .getClient()
      .get<GetProductsOnPromotionResponse>(
        `/stock/${storeSlug}/products-on-promotion/`,
        {
          baseURL: backendUrl,
          params: queryParams,
        }
      );
  }

  async getProductDetail({ productId, storeSlug }: GetProductDetailParams) {
    return await this.client
      .getClient()
      .get<Product>(`/stock/${storeSlug}/products/product-detail`, {
        baseURL: backendUrl,
        params: { product_id: productId },
      });
  }

  async getProductsByTag({
    tagSearch,
    token,
    storeSlug,
  }: GetProductsByTagParams) {
    return await this.client
      .getClient()
      .get<StoreStockResponse>(`/stock/${storeSlug}/products/by-tag/`, {
        ...(token && {
          headers: {
            Authorization: `Token ${token}`,
          },
        }),
        baseURL: backendUrl,
        params: {
          tag: tagSearch,
        },
      });
  }
}

export default ProductsService;
